import { Component, Input } from '@angular/core';
import {
  CancelRefundType,
  CancelType,
} from '../gantt/interfaces/gantt-details.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CancelServiceOption,
  CancelServicePayload,
  CancelServiceService,
} from './service/cancel-service.service';
import { finalize, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';

@Component({
  selector: 'app-modal-cancel-service',
  templateUrl: './modal-cancel-service.component.html',
  styleUrls: ['./modal-cancel-service.component.css'],
})
export class ModalCancelServiceComponent {
  @Input({ required: true }) orderHash!: string;
  @Input() nodeId!: string;

  form: FormGroup;
  saving = false;
  reasonsLoading = false;
  reasons: CancelServiceOption[] = [];
  CancelType = CancelType;
  otherReasonGuid: string | null = null;
  otherReasonCode = 'OTHER_REASON';

  //TODO: transelate keys, poner las correctas
  readonly cancelByOptions: Array<{ value: CancelType; labelKey: string }> = [
    {
      value: CancelType.CLIENT,
      labelKey: 'service_manager.cancel.modal.caused_by.client',
    },
    {
      value: CancelType.CAFLER,
      labelKey: 'service_manager.cancel.modal.caused_by.cafler',
    },
  ];

  readonly options: Array<{ value: CancelRefundType; labelKey: string }> = [
    { value: CancelRefundType.Refund0, labelKey: '0%' },
    { value: CancelRefundType.Refund100, labelKey: '100%' },
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly activeModal: NgbActiveModal,
    private readonly cancelService: CancelServiceService,
    private readonly translate: TranslateService,
    private readonly alertService: TuiAlertService
  ) {
    this.form = this.fb.group({
      causedBy: [null, Validators.required],
      reasonCode: [null, Validators.required],
      reasonText: [''],
      percentage: [null],
    });
    this.setupDynamicValidators();

    this.loadReasons();
  }

  private setupDynamicValidators(): void {
    this.form.get('causedBy')!.valueChanges.subscribe((value) => {
      const percentageCtrl = this.form.get('percentage')!;

      if (value === CancelType.CLIENT) {
        percentageCtrl.setValidators([Validators.required]);
      } else {
        percentageCtrl.setValue(CancelRefundType.Refund0);
        percentageCtrl.clearValidators();
      }

      percentageCtrl.updateValueAndValidity();
    });

    this.form.get('reasonCode')!.valueChanges.subscribe((value) => {
      const reasonTextCtrl = this.form.get('reasonText')!;

      if (value === this.otherReasonGuid) {
        reasonTextCtrl.setValue('');
        reasonTextCtrl.setValidators([Validators.required]);
      } else {
        reasonTextCtrl.clearValidators();
      }

      reasonTextCtrl.updateValueAndValidity();
    });
  }

  private loadReasons(): void {
    this.reasonsLoading = true;

    this.cancelService
      .getCancelReasons()
      .pipe(
        take(1),
        finalize(() => {
          this.reasonsLoading = false;
        })
      )
      .subscribe({
        next: (reasons: CancelServiceOption[]) => {
          this.reasons = reasons ?? [];
          this.setOtherReasonGuid(this.reasons);
        },
        error: (error: unknown) => {
          console.error('[ModalCancelService] loadReasons error for', error);

          this.reasons = [];

          const msg = this.translate.instant(
            'service_manager.cancel.error_loading_reasons'
          );

          this.alertService
            .open(msg, { status: 'error', autoClose: true })
            .subscribe();
        },
      });
  }

  setOtherReasonGuid(reasons: CancelServiceOption[] | null) {
    const match = reasons?.find((r) => r.code === this.otherReasonCode);
    this.otherReasonGuid = match ? match.id : null;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { causedBy, reasonCode, reasonText, percentage } = this.form.value;
    const customReasonText =
      reasonCode === this.otherReasonGuid && typeof reasonText === 'string'
        ? reasonText.trim()
        : null;

    const payload: CancelServicePayload = {
      CancelType: causedBy,
      CancelReasonGuid: reasonCode,
      ReasonText: customReasonText,
      RefundType: percentage,
    };

    this.saving = true;
    this.cancelService
      .cancelService(this.orderHash, payload)
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.activeModal.close('saved');
        },
        error: (err: unknown) => {
          console.error('[ModalCancelService] save error', err);

          const base = this.translate.instant(
            'service_manager.cancel.cancel_error'
          );
          const extra =
            typeof err === 'object' && err && 'message' in err
              ? (err as { message: string }).message
              : '';
          const msg = extra ? `${base}: ${extra}` : base;

          this.alertService
            .open(msg, { status: 'error', autoClose: true })
            .subscribe();
        },
      });
  }
}
